import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

import { HeaderComponent } from '../../../shared/components/header/header.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { SelectPesquisavelComponent } from '../../../shared/components/select-pesquisavel/select-pesquisavel.component';

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';

import { ReajustePrecoService } from '../../../shared/services/apis/produto/reajuste-preco.service';
import { UsuarioService } from '../../../shared/services/apis/usuario/usuario.service';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { GetReajustePrecoListaResponse } from '../../../shared/services/responses/produto/get-reajuste-preco-lista.response';
import { ReajustePrecoHistoricoStruct } from '../../../shared/services/structs/produto/reajuste-preco-historico.struct';
import { Usuario } from '../../../shared/services/models/usuario/usuario.model';

@Component({
  selector: 'app-reajuste-precos-lista',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatBadgeModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTableModule,
    HeaderComponent,
    EmptyListComponent,
    LoadingBoxDefaultListComponent,
    SelectPesquisavelComponent,
  ],
  templateUrl: './reajuste-precos-lista.component.html',
  styleUrl: './reajuste-precos-lista.component.css',
})
export class ReajustePrecosListaComponent implements OnInit {
  public moduloEnum = ModuloEnum.Cadastros;
  public funcionalidadeEnum = FuncionalidadeEnum.Cadastros_ReajustePreco;

  public model: FormGroup;
  public isLoading = false;
  public ultimaPagina = false;
  public usuarios: Usuario[] = [];

  public displayedColumns: string[] = ['dataHora', 'usuario', 'listaPreco', 'produtos', 'actions'];
  public dataSource = new MatTableDataSource<ReajustePrecoHistoricoStruct>();

  private paginaAtual = 1;
  private readonly tamanhoPagina = 20;
  private totalRegistros = 0;

  private origem: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private reajustePrecoService: ReajustePrecoService,
    private usuarioService: UsuarioService,
    private alertService: AlertService
  ) { }

  ngOnInit(): void {
    this.origem = this.route.snapshot.queryParamMap.get('origem');

    this.model = this.formBuilder.group({
      pesquisa: [''],
      listIdUsuario: [[]],
    });

    this.usuarioService.getAll().subscribe({
      next: res => {
        if (!res.isError) {
          this.usuarios = res.listaUsuario;
        }
      },
    });

    this.pesquisar();
  }

  public get qtdFiltrosAvancados(): number {
    const listIdUsuario: number[] = this.model.get('listIdUsuario')?.value || [];
    return listIdUsuario.length;
  }

  public aplicarFiltros(): void {
    this.clickPesquisar();
  }

  public limparFiltros(): void {
    this.model.get('listIdUsuario')?.setValue([]);
    this.clickPesquisar();
  }

  public clickPesquisar(): void {
    this.paginaAtual = 1;
    this.dataSource.data = [];
    this.ultimaPagina = false;
    this.pesquisar();
  }

  public proximaPagina(): void {
    this.paginaAtual++;
    this.pesquisar();
  }

  public novoReajuste(): void {
    this.router.navigate(['/cadastros/reajuste-precos/novo']);
  }

  public visualizar(idProdutoReajustePreco: number): void {
    this.router.navigate(['/cadastros/reajuste-precos', idProdutoReajustePreco]);
  }

  public voltar(): void {
    const destino = this.origem === 'produtos' ? '/cadastros/produtos' : '/cadastros';
    this.router.navigate([destino]);
  }

  public nomeUsuario(idUsuario: number): string {
    return this.usuarios.find(u => u.idUsuario === idUsuario)?.nomeUsuario ?? '—';
  }

  private pesquisar(): void {
    if (this.isLoading) return;
    this.isLoading = true;

    const pesquisa = this.model.get('pesquisa')?.value;
    const listIdUsuario: number[] = this.model.get('listIdUsuario')?.value || [];

    this.reajustePrecoService
      .getAll(pesquisa, listIdUsuario, null, null, this.paginaAtual, this.tamanhoPagina)
      .subscribe({
        next: (response: GetReajustePrecoListaResponse) => {
          this.isLoading = false;
          if (response.isError) {
            this.alertService.show('Erro', response.errorDescription, AlertType.error);
            return;
          }
          this.dataSource.data = [...this.dataSource.data, ...response.itens];
          this.totalRegistros = response.totalRegistros;
          this.ultimaPagina = this.dataSource.data.length >= this.totalRegistros;
        },
        error: err => {
          this.isLoading = false;
          this.alertService.show('Erro inesperado', err.message || err, AlertType.error);
        },
      });
  }
}
